<script setup>
import { computed, onMounted, reactive, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import {
  useFunctionGetter,
  useMapGetter,
  useStore,
} from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';

import IntegrationsAPI from 'dashboard/api/integrations';
import SettingsLayout from '../SettingsLayout.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import ModelSelector from '../wizard/components/ModelSelector.vue';
import { useWizardConfigStore } from 'dashboard/store/wizard/preferences';

const { t } = useI18n();
const store = useStore();
const wizardConfigStore = useWizardConfigStore();

const integrationLoaded = ref(false);
const isSaving = ref(false);
const isSavingModels = ref(false);

const integration = useFunctionGetter(
  'integrations/getIntegration',
  'vertexai'
);
const uiFlags = useMapGetter('integrations/getUIFlags');

const formState = reactive({
  projectId: '',
  location: 'global',
  labelSuggestion: false,
});

const modelFeatures = computed(() => [
  {
    key: 'editor',
    title: t('WIZARD_SETTINGS.MODEL_CONFIG.EDITOR.TITLE'),
    description: t('WIZARD_SETTINGS.MODEL_CONFIG.EDITOR.DESCRIPTION'),
  },
  {
    key: 'assistant',
    title: t('WIZARD_SETTINGS.MODEL_CONFIG.ASSISTANT.TITLE'),
    description: t('WIZARD_SETTINGS.MODEL_CONFIG.ASSISTANT.DESCRIPTION'),
  },
  {
    key: 'copilot',
    title: t('WIZARD_SETTINGS.MODEL_CONFIG.COPILOT.TITLE'),
    description: t('WIZARD_SETTINGS.MODEL_CONFIG.COPILOT.DESCRIPTION'),
  },
  {
    key: 'label_suggestion',
    title: t('WIZARD_SETTINGS.FEATURES.LABEL_SUGGESTION.MODEL_TITLE'),
    description: t(
      'WIZARD_SETTINGS.FEATURES.LABEL_SUGGESTION.MODEL_DESCRIPTION'
    ),
  },
]);

const connectedHook = computed(() => integration.value?.hooks?.[0] || null);
const isConnected = computed(() => !!connectedHook.value);

const hydrateForm = hook => {
  const settings = hook?.settings || {};
  formState.projectId = settings.project_id || '';
  formState.location = settings.location || 'global';
  formState.labelSuggestion = !!settings.label_suggestion;
};

const refreshPageData = async () => {
  await Promise.all([
    store.dispatch('integrations/get'),
    wizardConfigStore.fetch(),
  ]);
  hydrateForm(integration.value?.hooks?.[0]);
  integrationLoaded.value = true;
};

const buildHookPayload = () => ({
  app_id: 'vertexai',
  status: 'enabled',
  settings: {
    project_id: formState.projectId.trim(),
    location: formState.location,
    label_suggestion: formState.labelSuggestion,
  },
});

const saveConnection = async () => {
  if (!formState.projectId.trim()) {
    useAlert(t('INTEGRATION_SETTINGS.VERTEXAI.PROJECT_ID.REQUIRED'));
    return;
  }

  isSaving.value = true;
  try {
    const payload = buildHookPayload();

    if (connectedHook.value) {
      await IntegrationsAPI.updateHook(connectedHook.value.id, payload);
    } else {
      await IntegrationsAPI.createHook(payload);
    }

    await refreshPageData();
    useAlert(t('INTEGRATION_SETTINGS.VERTEXAI.SAVE.SUCCESS'));
  } catch (error) {
    useAlert(
      error?.response?.data?.message ||
        t('INTEGRATION_SETTINGS.VERTEXAI.SAVE.ERROR')
    );
  } finally {
    isSaving.value = false;
  }
};

const disconnect = async () => {
  if (!connectedHook.value) return;

  isSaving.value = true;
  try {
    await store.dispatch('integrations/deleteHook', {
      hookId: connectedHook.value.id,
      appId: connectedHook.value.app_id,
    });
    await refreshPageData();
    useAlert(t('INTEGRATION_SETTINGS.VERTEXAI.DISCONNECT.SUCCESS'));
  } catch (error) {
    useAlert(
      error?.response?.data?.message ||
        t('INTEGRATION_SETTINGS.VERTEXAI.DISCONNECT.ERROR')
    );
  } finally {
    isSaving.value = false;
  }
};

const updateModel = async ({ feature, model }) => {
  isSavingModels.value = true;
  try {
    await wizardConfigStore.updatePreferences({
      wizard_models: { [feature]: model },
    });
    useAlert(t('INTEGRATION_SETTINGS.VERTEXAI.MODELS.SUCCESS'));
  } catch (error) {
    useAlert(
      error?.response?.data?.message ||
        t('INTEGRATION_SETTINGS.VERTEXAI.MODELS.ERROR')
    );
    wizardConfigStore.fetch();
  } finally {
    isSavingModels.value = false;
  }
};

onMounted(() => {
  refreshPageData();
});
</script>

<template>
  <SettingsLayout :is-loading="!integrationLoaded || uiFlags.isFetching">
    <template #header>
      <BaseSettingsHeader
        :title="$t('INTEGRATION_SETTINGS.VERTEXAI.HEADER')"
        description=""
        feature-name="integrations"
        :back-button-label="$t('INTEGRATION_SETTINGS.HEADER')"
      />
    </template>
    <template #body>
      <div class="mx-6 max-w-4xl flex flex-col gap-6">
        <div
          class="rounded-xl bg-n-card outline outline-1 outline-n-container p-6"
        >
          <div class="flex flex-col gap-2 mb-6">
            <h3 class="text-heading-1 text-n-slate-12">
              {{ $t('INTEGRATION_SETTINGS.VERTEXAI.CONNECT.TITLE') }}
            </h3>
            <p class="text-body-main text-n-slate-11">
              {{ $t('INTEGRATION_SETTINGS.VERTEXAI.CONNECT.DESCRIPTION') }}
            </p>
          </div>

          <div class="flex flex-col gap-4">
            <Input
              v-model="formState.projectId"
              :label="$t('INTEGRATION_SETTINGS.VERTEXAI.PROJECT_ID.LABEL')"
              :placeholder="
                $t('INTEGRATION_SETTINGS.VERTEXAI.PROJECT_ID.PLACEHOLDER')
              "
            />

            <div class="flex flex-col min-w-0 gap-1">
              <label class="mb-0.5 text-heading-3 text-n-slate-12">
                {{ $t('INTEGRATION_SETTINGS.VERTEXAI.LOCATION.LABEL') }}
              </label>
              <select
                v-model="formState.location"
                class="block w-full h-10 px-3 py-2.5 text-sm rounded-lg bg-n-alpha-black2 text-n-slate-12 outline outline-1 outline-n-weak hover:outline-n-slate-6 focus:outline-n-brand"
              >
                <option value="global">
                  {{
                    $t('INTEGRATION_SETTINGS.VERTEXAI.LOCATION.OPTIONS.GLOBAL')
                  }}
                </option>
                <option value="us-central1">
                  {{
                    $t(
                      'INTEGRATION_SETTINGS.VERTEXAI.LOCATION.OPTIONS.US_CENTRAL1'
                    )
                  }}
                </option>
              </select>
              <p class="text-sm text-n-slate-11 mt-1">
                {{ $t('INTEGRATION_SETTINGS.VERTEXAI.LOCATION.HELP') }}
              </p>
            </div>

            <div
              class="flex items-start justify-between gap-4 rounded-lg bg-n-alpha-2 p-4"
            >
              <div>
                <h4 class="text-sm font-medium text-n-slate-12">
                  {{
                    $t('INTEGRATION_SETTINGS.VERTEXAI.LABEL_SUGGESTION.TITLE')
                  }}
                </h4>
                <p class="text-sm text-n-slate-11 mt-0.5">
                  {{
                    $t(
                      'INTEGRATION_SETTINGS.VERTEXAI.LABEL_SUGGESTION.DESCRIPTION'
                    )
                  }}
                </p>
              </div>
              <Switch v-model="formState.labelSuggestion" />
            </div>

            <div class="flex flex-wrap gap-2 justify-end">
              <Button
                v-if="isConnected"
                ruby
                faded
                :label="$t('INTEGRATION_SETTINGS.VERTEXAI.DISCONNECT.BUTTON')"
                :is-loading="isSaving"
                @click="disconnect"
              />
              <Button
                :label="$t('INTEGRATION_SETTINGS.VERTEXAI.SAVE.BUTTON')"
                :is-loading="isSaving"
                @click="saveConnection"
              />
            </div>
          </div>
        </div>

        <div
          class="rounded-xl bg-n-card outline outline-1 outline-n-container p-6"
        >
          <div class="flex flex-col gap-2 mb-6">
            <h3 class="text-heading-1 text-n-slate-12">
              {{ $t('INTEGRATION_SETTINGS.VERTEXAI.MODELS.TITLE') }}
            </h3>
            <p class="text-body-main text-n-slate-11">
              {{ $t('INTEGRATION_SETTINGS.VERTEXAI.MODELS.DESCRIPTION') }}
            </p>
          </div>

          <div v-if="!isConnected" class="rounded-lg bg-n-alpha-2 p-4">
            <p class="text-sm text-n-slate-11">
              {{ $t('INTEGRATION_SETTINGS.VERTEXAI.MODELS.EMPTY_STATE') }}
            </p>
          </div>

          <div v-else class="grid gap-4">
            <ModelSelector
              v-for="feature in modelFeatures"
              :key="feature.key"
              :feature-key="feature.key"
              :title="feature.title"
              :description="feature.description"
              :is-allowed="!isSavingModels"
              :allowed-providers="['vertexai']"
              @change="updateModel"
            />
          </div>
        </div>
      </div>
    </template>
  </SettingsLayout>
</template>
